import { useCallback, useRef, useState } from 'react';

type BaseModalWithResultProps<ResultType> = {
  onCancel: () => void;
  onComplete: (result: ResultType) => void;
};

export type ModalWithResultProps<
  ResultType,
  ParamType = Record<string, unknown>,
> = ParamType & BaseModalWithResultProps<ResultType>;

export function useModalWithResult<
  ResultType,
  ParamType = Record<string, never>,
>(): {
  open: (params: ParamType) => Promise<null | ResultType>;
  close: () => void;
  modalProps: null | ModalWithResultProps<ResultType, ParamType>;
} {
  const [modalParams, setModalParams] = useState<ParamType | null>(null);
  const modalResolve = useRef<
    undefined | ((result: null | ResultType) => void)
  >(undefined);

  const open = useCallback((params: ParamType) => {
    const p = new Promise<null | ResultType>((resolve) => {
      modalResolve.current = (result) => {
        modalResolve.current = undefined;
        resolve(result);
        setModalParams(null);
      };
    });
    setModalParams(params);
    return p;
  }, []);

  const close = useCallback(() => {
    modalResolve.current?.(null);
  }, []);

  return {
    open,
    close,
    modalProps:
      modalParams === null
        ? null
        : {
            ...modalParams,
            onCancel: () => {
              modalResolve.current?.(null);
            },
            onComplete: (result) => {
              modalResolve.current?.(result);
            },
          },
  };
}
